import {selectArrayTernary} from '@coolcolduk/util';
import {Box, Card, CardContent, Chip, LinearProgress, Skeleton, Typography} from '@mui/material';
import {Progress} from '@cp/model';

interface TopicBreakdownCardProps {
  progressList: Progress[];
  loading: boolean;
}

export default function TopicBreakdownCard({progressList, loading}: TopicBreakdownCardProps) {
  if (loading) {
    return (
      <Card sx={{height: '100%'}}>
        <CardContent sx={{p: 3}}>
          <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
            Topic Breakdown
          </Typography>
          <Box sx={{space: 2}}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{mb: 2}}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="rectangular" height={8} sx={{mt: 1, borderRadius: 1}} />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!progressList || progressList.length === 0) {
    return (
      <Card sx={{height: '100%'}}>
        <CardContent sx={{p: 3}}>
          <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
            Topic Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
            No progress data available yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Calculate topic performance
  const topicPerformance = new Map<string, {score: number; accuracy: number; questions: number; difficulty: number}>();

  progressList.forEach((progress) => {
    progress.scores.forEach((score) => {
      const existing = topicPerformance.get(score.topic);
      if (existing) {
        // Average the scores
        existing.score = (existing.score + score.score) / 2;
        existing.accuracy = (existing.accuracy + score.accuracy * 100) / 2;
        existing.questions += score.total;
        existing.difficulty = Math.max(existing.difficulty, score.difficulty);
      } else {
        topicPerformance.set(score.topic, {
          score: score.score,
          accuracy: score.accuracy * 100,
          questions: score.total,
          difficulty: score.difficulty,
        });
      }
    });
  });

  // Sort by score and get top 5
  const topTopics = Array.from(topicPerformance.entries())
    .map(([topic, data]) => ({
      topic,
      ...data,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  const getPerformanceColor = (score: number) => {
    if (score >= 4) return 'success';
    if (score >= 3) return 'primary';
    if (score >= 2) return 'warning';
    return 'error';
  };

  const getPerformanceLabel = (score: number) => {
    if (score >= 4) return 'Excellent';
    if (score >= 3) return 'Good';
    if (score >= 2) return 'Needs Work';
    return 'Keep Practicing';
  };

  return (
    <Card sx={{height: '100%'}}>
      <CardContent sx={{p: 3}}>
        <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
          Top Topics
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          Your best performing topics
        </Typography>

        <Box sx={{space: 2}}>
          {topTopics.map((topic) => (
            <Box key={topic.topic} sx={{mb: 3}}>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                <Typography variant="body2" sx={{fontWeight: 500, flex: 1}}>
                  {topic.topic.length > 20 ? `${topic.topic.substring(0, 20)}...` : topic.topic}
                </Typography>
                <Chip
                  label={getPerformanceLabel(topic.score)}
                  size="small"
                  color={getPerformanceColor(topic.score)}
                  variant="outlined"
                />
              </Box>

              <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 1}}>
                <Box sx={{flex: 1}}>
                  <LinearProgress
                    variant="determinate"
                    value={(topic.score / 5) * 100}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        bgcolor: selectArrayTernary(
                          [
                            getPerformanceColor(topic.score) === 'success',
                            getPerformanceColor(topic.score) === 'primary',
                            getPerformanceColor(topic.score) === 'warning',
                          ],
                          ['success.main', 'primary.main', 'warning.main'],
                          'error.main',
                        ),
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{fontWeight: 600, minWidth: 40}}>
                  {topic.score.toFixed(1)}
                </Typography>
              </Box>

              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(topic.accuracy)}% accuracy
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {topic.questions} questions
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {topTopics.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 2}}>
            Complete some questions to see your topic breakdown
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
